import { useCallback, useContext, useMemo, useState } from 'react';

import { CreatorDiscoveryEmailResource, useCommunitiesQuery, useProgramsQuery } from '@frontend/app/hooks';
import { useProspectsAddToCommunity } from '@frontend/app/hooks/useProspectsAddToCommunity';
import { useProspectsAddToProgram } from '@frontend/app/hooks/useProspectsAddToProgram';
import { useProspectsInviteToProgram } from '@frontend/app/hooks/useProspectsInviteToProgram';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { GetCommunitiesQuery_communities } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { IMessagingContext, MessagingContext } from '@frontend/context/MessagingContext';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { UseGenericTableConfig, useGenericTable } from '../../../../hooks/useGenericTable';
import { transformCreatorsToProspects } from '../utils/transform-creators-to-prospects';
import type { BulkOperationState } from '../../bulk-operation-alert';

const MUTATION_SOURCE = 'instagram_creator_search_table';

// Extend the config type to include search terms and their setters, and selection state
export interface UseInstagramCreatorSearchTableConfig extends UseGenericTableConfig<InstagramCreator> {
  campaignSearchTerm?: string;
  setCampaignSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  groupSearchTerm?: string;
  setGroupSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  emailResource?: CreatorDiscoveryEmailResource;
  igdmActive?: boolean;
}

export const useInstagramCreatorSearchTable = (config: UseInstagramCreatorSearchTableConfig) => {
  const { tableProps } = useGenericTable<InstagramCreator>(config);
  const [selectedRows, setSelectedRows] = useState<InstagramCreator[]>([]);
  const messagingContext = useContext<IMessagingContext | undefined>(MessagingContext);

  // Bulk operation state
  const [bulkOperationState, setBulkOperationState] = useState<BulkOperationState>({
    isVisible: false,
    state: 'loading',
    operationType: 'invite-to-campaign',
    count: 0,
    targetTitle: '',
  });

  // State for confirmation dialogs
  const [isInviteToCampaignConfirmOpen, setIsInviteToCampaignConfirmOpen] = useState(false);
  const [pendingInviteToCampaign, setPendingInviteToCampaign] = useState<GetAllPrograms_programs | null>(null);
  const [isAddToGroupConfirmOpen, setIsAddToGroupConfirmOpen] = useState(false);
  const [pendingAddToGroup, setPendingAddToGroup] = useState<GetCommunitiesQuery_communities | null>(null);
  const [isAddToCampaignConfirmOpen, setIsAddToCampaignConfirmOpen] = useState(false);
  const [pendingAddToCampaign, setPendingAddToCampaign] = useState<GetAllPrograms_programs | null>(null);

  // Filter selected rows to only include those that can be invited
  // If IGDM is active: include creators with email OR creators in IG marketplace
  // If IGDM is not active: include only creators with email
  const selectedRowsThatCanBeInvited = useMemo(() => {
    return selectedRows.filter((creator) => {
      const hasEmail = creator.email && creator.email.trim() !== '';
      const inCreatorMarketplace = creator.inCreatorMarketplace === true;

      if (config.igdmActive) {
        return hasEmail || inCreatorMarketplace;
      } else {
        return hasEmail;
      }
    });
  }, [selectedRows, config.igdmActive]);

  const showBulkOperationAlert = (
    operationType: BulkOperationState['operationType'],
    count: number,
    targetTitle: string,
  ) => {
    setBulkOperationState({
      isVisible: true,
      state: 'loading',
      operationType,
      count,
      targetTitle,
    });
  };

  const hideBulkOperationAlert = () => {
    setBulkOperationState((prev) => ({ ...prev, isVisible: false }));
  };

  const updateBulkOperationState = (
    state: BulkOperationState['state'],
    successCount?: number,
    errorCount?: number,
    errorMessage?: string,
  ) => {
    setBulkOperationState((prev) => ({
      ...prev,
      state,
      successCount: successCount !== undefined ? successCount : (prev.successCount ?? 0),
      errorCount: errorCount !== undefined ? errorCount : (prev.errorCount ?? 0),
      errorMessage,
    }));
  };

  // Store the expected count for current invite operation to avoid stale closures
  const [expectedInviteCount, setExpectedInviteCount] = useState<number>(0);

  const [inviteToProgram, { loading: inviteLoading, error: _inviteError }] = useProspectsInviteToProgram({
    onCompleted: (data) => {
      const successCount = data?.members?.length || 0;
      const errorCount = expectedInviteCount - successCount;

      if (errorCount === 0) {
        updateBulkOperationState('success', successCount, 0);
        messagingContext?.showSuccessMessage('Successfully invited creators to the campaign!');
      } else if (successCount > 0) {
        updateBulkOperationState('warning', successCount, errorCount);
        messagingContext?.showInfoMessage(`${successCount} creators invited successfully. ${errorCount} failed.`);
      } else {
        updateBulkOperationState('error', 0, errorCount, 'Failed to invite creators to campaign');
        messagingContext?.showErrorMessage('Failed to invite creators to campaign. Please try again.');
      }
      setIsInviteToCampaignConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to invite to campaign:', error);
      updateBulkOperationState('error', 0, expectedInviteCount, error.message);

      if (error.message.includes('Please, connect at least one email account to send invites')) {
        messagingContext?.showErrorMessage('Please connect an email account in settings to send invites.');
      } else {
        messagingContext?.showErrorMessage(
          messagingContext.messageFromError(error) || 'Failed to invite creators. Please try again.',
        );
      }
      setIsInviteToCampaignConfirmOpen(false);
    },
  });

  // Store the expected count for current add to program operation to avoid stale closures
  const [expectedAddToProgramCount, setExpectedAddToProgramCount] = useState<number>(0);

  const [addToProgram, { loading: addToProgramLoading, error: _addToProgramError }] = useProspectsAddToProgram({
    onCompleted: (data) => {
      const successCount = data?.members?.length || 0;
      const errorCount = expectedAddToProgramCount - successCount;

      if (errorCount === 0) {
        updateBulkOperationState('success', successCount, 0);
        messagingContext?.showSuccessMessage('Successfully added creators to the campaign!');
      } else if (successCount > 0) {
        updateBulkOperationState('warning', successCount, errorCount);
        messagingContext?.showInfoMessage(`${successCount} creators added successfully. ${errorCount} failed.`);
      } else {
        updateBulkOperationState('error', 0, errorCount, 'Failed to add creators to campaign');
        messagingContext?.showErrorMessage('Failed to add creators to campaign. Please try again.');
      }
      setIsAddToCampaignConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to add to campaign:', error);
      updateBulkOperationState('error', 0, expectedAddToProgramCount, error.message);
      messagingContext?.showErrorMessage(
        messagingContext.messageFromError(error) || 'Failed to add creators to campaign. Please try again.',
      );
      setIsAddToCampaignConfirmOpen(false);
    },
  });

  // Store the expected count for current add to community operation to avoid stale closures
  const [expectedAddToCommunityCount, setExpectedAddToCommunityCount] = useState<number>(0);

  const [addToCommunity, { loading: addToCommunityLoading, error: _addToCommunityError }] = useProspectsAddToCommunity({
    onCompleted: (data) => {
      const successCount = data?.members?.length || 0;
      const errorCount = expectedAddToCommunityCount - successCount;

      if (errorCount === 0) {
        updateBulkOperationState('success', successCount, 0);
        messagingContext?.showSuccessMessage('Successfully added creators to the group!');
      } else if (successCount > 0) {
        updateBulkOperationState('warning', successCount, errorCount);
        messagingContext?.showInfoMessage(`${successCount} creators added successfully. ${errorCount} failed.`);
      } else {
        updateBulkOperationState('error', 0, errorCount, 'Failed to add creators to group');
        messagingContext?.showErrorMessage('Failed to add creators to group. Please try again.');
      }
      setIsAddToGroupConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to add to group:', error);
      updateBulkOperationState('error', 0, expectedAddToCommunityCount, error.message);
      messagingContext?.showErrorMessage(
        messagingContext.messageFromError(error) || 'Failed to add creators to group. Please try again.',
      );
      setIsAddToGroupConfirmOpen(false);
    },
  });

  const onSelectionChange = useCallback((rows: InstagramCreator[]) => {
    setSelectedRows((prevRows) => {
      if (rows.length !== prevRows.length || rows.some((row, index) => row !== prevRows[index])) {
        return rows;
      }
      return prevRows;
    });
  }, []);

  const { data: campaignsData } = useProgramsQuery();
  const { data: groupsData } = useCommunitiesQuery();

  const filteredCampaigns = useMemo(() => {
    if (!campaignsData?.programs) return [];
    const searchTerm = config.campaignSearchTerm?.trim().toLowerCase();
    if (!searchTerm) return campaignsData.programs;
    return campaignsData.programs.filter((program) => program.title.toLowerCase().includes(searchTerm));
  }, [campaignsData, config.campaignSearchTerm]);

  const filteredGroups = useMemo(() => {
    if (!groupsData?.communities) return [];
    const searchTerm = config.groupSearchTerm?.trim().toLowerCase();
    if (!searchTerm) return groupsData.communities;
    return groupsData.communities.filter((group) => group.title.toLowerCase().includes(searchTerm));
  }, [groupsData, config.groupSearchTerm]);

  // Modified to open dialog
  const handleInviteToCampaign = (program: GetAllPrograms_programs) => {
    if (!messagingContext) {
      console.error('MessagingContext not available');
      return;
    }

    if (program.status !== 'active') {
      messagingContext.showErrorMessage('Cannot invite to a campaign that is not active.');
      return;
    }

    if (!program.published) {
      messagingContext.showErrorMessage('Cannot invite to a campaign that has not been published.');
      return;
    }

    if (selectedRows.length === 0) {
      messagingContext.showInfoMessage('No creators selected for invitation.');
      return;
    }

    if (selectedRowsThatCanBeInvited.length === 0) {
      const message = config.igdmActive
        ? 'No creators with email addresses or in Instagram Creator Marketplace selected for invitation.'
        : 'No creators with email addresses selected for invitation.';
      messagingContext.showInfoMessage(message);
      return;
    }
    setPendingInviteToCampaign(program);
    setIsInviteToCampaignConfirmOpen(true);
  };

  // New function to proceed with the actual invite
  const proceedWithInviteToCampaign = async () => {
    if (!pendingInviteToCampaign || selectedRowsThatCanBeInvited.length === 0) {
      setIsInviteToCampaignConfirmOpen(false);
      return;
    }

    // Capture expected count before starting operation to avoid stale closures
    const expectedCount = selectedRowsThatCanBeInvited.length;
    setExpectedInviteCount(expectedCount);

    // Show loading alert
    showBulkOperationAlert('invite-to-campaign', expectedCount, pendingInviteToCampaign.title);

    const prospectsForApi = transformCreatorsToProspects(selectedRowsThatCanBeInvited);

    try {
      await inviteToProgram({
        variables: {
          prospects: prospectsForApi,
          programIds: [pendingInviteToCampaign.id],
          resourceId: config.emailResource?.id,
          source: 'creator_search',
        },
      });
    } catch (e) {
      console.error('Error during campaign invite process:', e);
      updateBulkOperationState('error', 0, expectedCount, 'An unexpected error occurred. Please try again.');
      setIsInviteToCampaignConfirmOpen(false);
    }
  };

  // Modified to open dialog
  const handleAddToGroup = (community: GetCommunitiesQuery_communities) => {
    if (!messagingContext) {
      console.error('MessagingContext not available');
      return;
    }
    if (selectedRows.length === 0) {
      messagingContext.showInfoMessage('No creators selected to add to group.');
      return;
    }
    setPendingAddToGroup(community);
    setIsAddToGroupConfirmOpen(true);
  };

  // New function to proceed with adding to group
  const proceedWithAddToGroup = async () => {
    if (!pendingAddToGroup || selectedRows.length === 0) {
      setIsAddToGroupConfirmOpen(false);
      return;
    }

    // Capture expected count before starting operation to avoid stale closures
    const expectedCount = selectedRows.length;
    setExpectedAddToCommunityCount(expectedCount);

    // Show loading alert
    showBulkOperationAlert('add-to-group', expectedCount, pendingAddToGroup.title);

    const prospectsForApi = transformCreatorsToProspects(selectedRows);

    try {
      await addToCommunity({
        variables: {
          prospects: prospectsForApi,
          communityIds: [pendingAddToGroup.id],
          source: MUTATION_SOURCE,
        },
      });
    } catch (e) {
      console.error('Error during add to group process:', e);
      updateBulkOperationState('error', 0, expectedCount, 'An unexpected error occurred. Please try again.');
      setIsAddToGroupConfirmOpen(false);
    }
  };

  // Modified to open dialog for add to campaign
  const handleAddToCampaign = (program: GetAllPrograms_programs) => {
    if (!messagingContext) {
      console.error('MessagingContext not available');
      return;
    }

    if (selectedRows.length === 0) {
      messagingContext.showInfoMessage('No creators selected to add to campaign.');
      return;
    }
    setPendingAddToCampaign(program);
    setIsAddToCampaignConfirmOpen(true);
  };

  // New function to proceed with the actual add to campaign
  const proceedWithAddToCampaign = async () => {
    if (!pendingAddToCampaign || selectedRows.length === 0) {
      setIsAddToCampaignConfirmOpen(false);
      return;
    }

    // Capture expected count before starting operation to avoid stale closures
    const expectedCount = selectedRows.length;
    setExpectedAddToProgramCount(expectedCount);

    // Show loading alert
    showBulkOperationAlert('add-to-campaign', expectedCount, pendingAddToCampaign.title);

    const prospectsForApi = transformCreatorsToProspects(selectedRows);

    try {
      await addToProgram({
        variables: {
          prospects: prospectsForApi,
          programIds: [pendingAddToCampaign.id],
          source: MUTATION_SOURCE,
        },
      });
    } catch (e) {
      console.error('Error during add to campaign process:', e);
      updateBulkOperationState('error', 0, expectedCount, 'An unexpected error occurred. Please try again.');
      setIsAddToCampaignConfirmOpen(false);
    }
  };

  const hasSelectedRows = selectedRows.length > 0;
  const hasSelectedRowsThatCanBeInvited = selectedRowsThatCanBeInvited.length > 0;

  return {
    instagramTableProps: {
      ...tableProps,
      onSelectionChange,
      selectedRows,
    },
    actionMenuProps: {
      handleInviteToCampaign,
      handleAddToGroup,
      handleAddToCampaign,
      filteredCampaigns,
      filteredGroups,
      campaignSearchTerm: config.campaignSearchTerm || '',
      setCampaignSearchTerm: config.setCampaignSearchTerm,
      groupSearchTerm: config.groupSearchTerm || '',
      setGroupSearchTerm: config.setGroupSearchTerm,
      hasSelectedRows,
      hasSelectedRowsThatCanBeInvited,
      // Props for invite to campaign confirmation dialog
      isInviteToCampaignConfirmOpen,
      pendingInviteToCampaign,
      proceedWithInviteToCampaign,
      closeInviteToCampaignConfirmDialog: () => setIsInviteToCampaignConfirmOpen(false),
      // Props for add to group confirmation dialog
      isAddToGroupConfirmOpen,
      pendingAddToGroup,
      proceedWithAddToGroup,
      closeAddToGroupConfirmDialog: () => setIsAddToGroupConfirmOpen(false),
      // Props for add to campaign confirmation dialog
      isAddToCampaignConfirmOpen,
      pendingAddToCampaign,
      proceedWithAddToCampaign,
      closeAddToCampaignConfirmDialog: () => setIsAddToCampaignConfirmOpen(false),
      selectedRowsCount: selectedRows.length,
      selectedRowsThatCanBeInvitedCount: selectedRowsThatCanBeInvited.length,
      inviteLoading,
      addToCommunityLoading,
      addToProgramLoading,
      igdmActive: config.igdmActive || false,
    },
    bulkOperationState,
    hideBulkOperationAlert,
  };
};
